import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Message } from 'semantic-ui-react'
import { markDealWon } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { parseSites, suggestTemplateIds } from 'utility/crm/template-suggestions'
import { ALL_CRM_SCOPES, invalidateCrm, useAccountDetail, useCrmLookups } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'
import { Muted, Pill, Row } from './ui'

const field = { width: '100%', padding: 8 }
const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }

/**
 * Won handoff: creates (or reuses) the eAuditor customer, marks the account and its contacts Customer,
 * logs the timeline and creates 5 onboarding tasks. Templates are suggested by the account's industry.
 * @param {{ deal: import('utility/crm/types').CrmDeal|null, onClose: () => void }} props
 */
export const WonDialog = ({ deal, onClose }) => {
    const { lookups } = useCrmLookups()
    const account = useAccountDetail(deal ? deal.AccountId : null)
    const detail = account.data && deal && account.data.Account.AccountId === deal.AccountId ? account.data : null
    const templates = lookups?.Templates || []

    const [customerName, setCustomerName] = useState('')
    const [ownerId, setOwnerId] = useState('')
    const [sitesText, setSitesText] = useState('')
    const [selected, setSelected] = useState(() => new Set())
    const [inspectionDate, setInspectionDate] = useState('')
    const [inspectionBy, setInspectionBy] = useState('')
    const [filter, setFilter] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)
    const [result, setResult] = useState(null)
    const initializedFor = useRef(null)

    // Pre-fill once per deal, as soon as the account and the template list are known.
    useEffect(() => {
        if (!deal) {
            initializedFor.current = null
            return
        }
        if (!detail || !lookups || initializedFor.current === deal.DealId) return
        initializedFor.current = deal.DealId
        setCustomerName(detail.Account.Name)
        setOwnerId(deal.OwnerUserId ?? '')
        setSitesText((detail.Account.Sites || []).join('\n'))
        setSelected(new Set(suggestTemplateIds(detail.Account.Industry, templates)))
        setInspectionDate('')
        setInspectionBy('')
        setFilter('')
        setError(null)
        setResult(null)
    }, [deal, detail, lookups]) // eslint-disable-line

    const suggestedIds = useMemo(() => new Set(suggestTemplateIds(detail?.Account.Industry, templates)), [detail, templates])
    const visibleTemplates = templates.filter(t => t.TemplateName.toLowerCase().includes(filter.trim().toLowerCase()))
    const sites = parseSites(sitesText)
    const canSubmit = Boolean(customerName.trim()) && sites.length > 0 && !busy && Boolean(detail)

    const toggle = id =>
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    const submit = async () => {
        setBusy(true)
        setError(null)
        try {
            const done = await markDealWon(deal.DealId, {
                CustomerName: customerName.trim(),
                Sites: sites,
                TemplateIds: Array.from(selected),
                FirstInspectionDate: inspectionDate || undefined,
                FirstInspectionBy: inspectionBy || undefined,
                SuccessOwnerUserId: ownerId === '' ? undefined : Number(ownerId),
            })
            setResult(done)
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            const apiError = toApiError(err)
            setError(apiError.message)
            if (apiError.status === 409) invalidateCrm('deals', 'forecast') // already won / not open: show the real state
        } finally {
            setBusy(false)
        }
    }

    const footer = result ? (
        <Button type="button" primary onClick={onClose}>
            Done
        </Button>
    ) : (
        <>
            <Button type="button" onClick={onClose} disabled={busy}>
                Not now (keep deal open)
            </Button>
            <Button type="button" primary onClick={submit} loading={busy} disabled={!canSubmit}>
                Mark won &amp; create customer
            </Button>
        </>
    )

    return (
        <CrmModal open={Boolean(deal)} onClose={busy ? () => {} : onClose} title={deal ? `Won: hand ${deal.AccountName} to operations` : 'Won'} width={640} footer={footer}>
            {result ? (
                <div role="status">
                    <Message positive>
                        <Message.Header>Deal won and customer created</Message.Header>
                        {result.TasksCreated} onboarding task{result.TasksCreated === 1 ? '' : 's'} created for the success owner.
                    </Message>
                    {result.Templates?.length ? (
                        <>
                            <strong>Templates assigned</strong>
                            <ul>
                                {result.Templates.map(name => (
                                    <li key={String(name)}>{String(name)}</li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <Muted>No templates were assigned.</Muted>
                    )}
                </div>
            ) : (
                <>
                    <Row $gap={8} $wrap style={{ marginBottom: 6 }} aria-hidden="true">
                        <Pill $tone="green">1 Deal won</Pill>
                        <Pill $tone="blue">2 Set up customer in eAuditor</Pill>
                        <Pill>3 Onboarding tasks</Pill>
                    </Row>
                    {error ? (
                        <Message negative role="alert">
                            {error}
                        </Message>
                    ) : null}
                    {account.loading && !detail ? <Muted role="status">Loading account…</Muted> : null}
                    {account.error && !detail ? (
                        <Message negative role="alert">
                            {account.error.message}
                        </Message>
                    ) : null}

                    <label htmlFor="crm-won-name" style={label}>
                        Customer name
                    </label>
                    <input id="crm-won-name" value={customerName} onChange={e => setCustomerName(e.target.value)} style={field} maxLength={150} />

                    <label htmlFor="crm-won-owner" style={label}>
                        Customer success owner
                    </label>
                    <select id="crm-won-owner" value={ownerId} onChange={e => setOwnerId(e.target.value)} style={field}>
                        <option value="">Unassigned</option>
                        {(lookups?.Users || []).map(u => (
                            <option key={u.UserId} value={u.UserId}>
                                {u.FullName}
                            </option>
                        ))}
                    </select>

                    <label htmlFor="crm-won-sites" style={label}>
                        Sites / locations (one per line)
                    </label>
                    <textarea id="crm-won-sites" rows={4} value={sitesText} onChange={e => setSitesText(e.target.value)} style={field} />
                    {sites.length === 0 ? (
                        <p role="alert" style={{ color: '#dc2626', margin: '2px 0', fontSize: '0.8rem' }}>
                            At least one site is required.
                        </p>
                    ) : null}

                    <span style={label}>Audit templates to assign ({selected.size} selected)</span>
                    <input type="search" aria-label="Search templates" placeholder="Search templates…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...field, marginBottom: 6 }} />
                    <div role="group" aria-label="Audit templates" style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid rgba(128,128,128,.35)', borderRadius: 8, padding: 6 }}>
                        {visibleTemplates.length === 0 ? <Muted>No templates match.</Muted> : null}
                        {visibleTemplates.map(t => (
                            <div key={t.TemplateId} style={{ padding: '4px 6px' }}>
                                <Checkbox label={t.TemplateName} checked={selected.has(t.TemplateId)} onChange={() => toggle(t.TemplateId)} />
                                {suggestedIds.has(t.TemplateId) ? (
                                    <Pill $tone="blue" style={{ marginLeft: 8 }}>
                                        Suggested
                                    </Pill>
                                ) : null}
                            </div>
                        ))}
                    </div>

                    <Row $gap={12} $wrap style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 160 }}>
                            <label htmlFor="crm-won-date" style={label}>
                                First inspection
                            </label>
                            <input id="crm-won-date" type="date" value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} style={field} />
                        </div>
                        <div style={{ flex: 1, minWidth: 160 }}>
                            <label htmlFor="crm-won-by" style={label}>
                                First inspection by
                            </label>
                            <select id="crm-won-by" value={inspectionBy} onChange={e => setInspectionBy(e.target.value)} style={field}>
                                <option value="">Not decided</option>
                                {(detail?.Contacts || []).map(c => (
                                    <option key={c.ContactId} value={c.Name}>
                                        {c.Name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Row>
                </>
            )}
        </CrmModal>
    )
}
